import React, { useState } from "react";
import {
  X,
  Building2,
  Smartphone,
  Copy,
  Check,
  Clock,
  AlertCircle,
  Upload,
  Hash,
  MousePointerClick,
  QrCode
} from "lucide-react";

const PaymentInstructionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("bank"); // 'bank' or 'upi'
  const [copiedField, setCopiedField] = useState(null);

  if (!isOpen) return null;

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      {/* Modal Container */}
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Add Funds Instructions</h2>
            <p className="text-sm text-slate-500 mt-1">Follow these steps to complete your payment.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="p-6 overflow-y-auto">
          
          {/* Modern Segmented Tabs */}
          <div className="flex p-1 mb-8 space-x-1 bg-slate-100 rounded-xl">
            <button
              onClick={() => setActiveTab("bank")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                activeTab === "bank"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Building2 size={18} />
              Bank Transfer
            </button>
            <button
              onClick={() => setActiveTab("upi")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                activeTab === "upi"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Smartphone size={18} />
              UPI Payment
            </button>
          </div>

          {/* TAB CONTENT: BANK TRANSFER */}
          {activeTab === "bank" && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              
              <Step number="1" title="Select Pay Mode & Copy Details">
                <p className="text-sm text-slate-600 mb-3">
                  Select <span className="font-semibold text-slate-900">Bank Transfer</span> as your pay mode and transfer a minimum of <span className="font-semibold text-slate-900">₹2,000</span> to the following account via Netbanking:
                </p>
                {/* Bank Details Card */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 grid gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">Account Number</p>
                      <p className="font-mono text-slate-900 font-semibold mt-0.5">50200120431741</p>
                    </div>
                    <CopyButton 
                      isCopied={copiedField === 'acc'} 
                      onClick={() => handleCopy('50200120431741', 'acc')} 
                    />
                  </div>
                  <div className="h-px bg-slate-200 w-full"></div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase">IFSC Code</p>
                      <p className="font-mono text-slate-900 font-semibold mt-0.5">HDFC0000344</p>
                    </div>
                    <CopyButton 
                      isCopied={copiedField === 'ifsc'} 
                      onClick={() => handleCopy('HDFC0000344', 'ifsc')} 
                    />
                  </div>
                </div>
              </Step>

              <Step number="2" title="Return to Portal & Enter Details">
                <p className="text-sm text-slate-600 mb-3">After a successful transfer, come back and fill in the required payment details:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoTag icon={Hash} text="Enter Paid Amount" />
                  <InfoTag icon={Upload} text="Upload PaySlip (Required)" />
                  <InfoTag icon={AlertCircle} text="Unique UTR No. (Required)" />
                  <InfoTag icon={Clock} text="Txn Date (Auto-selected)" />
                </div>
              </Step>

              <Step number="3" title="Complete Request" isLast>
                <p className="text-sm text-slate-600 mb-4">
                  Click the <span className="font-semibold text-slate-900 px-2 py-1 bg-slate-100 rounded-md">Add Funds Now</span> button to submit your request.
                </p>
                <div className="flex items-start gap-3 p-3.5 bg-blue-50 text-blue-800 rounded-xl border border-blue-100">
                  <Clock size={20} className="mt-0.5 shrink-0" />
                  <p className="text-sm font-medium">
                    Funds will be added to your API Account within <span className="font-bold">50 minutes</span> after successful verification.
                  </p>
                </div>
              </Step>

            </div>
          )}

          {/* TAB CONTENT: UPI */}
          {activeTab === "upi" && (
            <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
              
              <Step number="1" title="Select Pay Mode & Enter Amount">
                <p className="text-sm text-slate-600">
                  Select <span className="font-semibold text-slate-900">UPI</span> as your pay mode. Enter an amount of at least <span className="font-semibold text-slate-900">₹2,000</span> in the portal. The transaction date will be selected automatically.
                </p>
              </Step>

              <Step number="2" title="Generate QR Code">
                <p className="text-sm text-slate-600">
                  Click the <span className="font-semibold text-slate-900 px-2 py-1 bg-slate-100 rounded-md">Add Funds Now</span> button. A unique UPI QR code will appear on your screen.
                </p>
              </Step>

              <Step number="3" title="Scan and Pay" isLast>
                <p className="text-sm text-slate-600 mb-4">
                  Open any UPI app (GPay, PhonePe, Paytm, etc.), scan the QR code, and complete the payment within given timeframe.
                </p>
                <div className="flex items-start gap-3 p-3.5 bg-emerald-50 text-emerald-800 rounded-xl border border-emerald-100">
                  <Clock size={20} className="mt-0.5 shrink-0" />
                  <p className="text-sm font-medium">
                    Your balance will be updated <span className="font-bold">shortly after</span> the payment is successfully completed.
                  </p>
                </div>
              </Step>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95"
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  );
};

/* --- Helper Components for Clean UI --- */

const Step = ({ number, title, children, isLast = false }) => (
  <div className="relative flex gap-4">
    {/* Vertical Line */}
    {!isLast && (
      <div className="absolute left-[15px] top-10 bottom-[-24px] w-px bg-slate-200"></div>
    )}
    
    {/* Step Number */}
    <div className="relative z-10 w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shrink-0 ring-4 ring-white">
      {number}
    </div>
    
    {/* Step Content */}
    <div className="pb-6 w-full">
      <h3 className="text-base font-bold text-slate-900 mb-2">{title}</h3>
      {children}
    </div>
  </div>
);

const CopyButton = ({ isCopied, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
      isCopied 
        ? "bg-emerald-100 text-emerald-700" 
        : "bg-slate-200 text-slate-700 hover:bg-slate-300"
    }`}
  >
    {isCopied ? <Check size={14} /> : <Copy size={14} />}
    {isCopied ? "Copied" : "Copy"}
  </button>
);

const InfoTag = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white border border-slate-200 shadow-xs">
    <Icon size={16} className="text-slate-400" />
    <span className="text-xs font-medium text-slate-700">{text}</span>
  </div>
);

export default PaymentInstructionsModal;